import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApolloError } from 'apollo-boost';

import {
	useCheckOrderAvailabilityMutation,
	OrderStatus,
	useSaveOrderServicesMutation,
	OrderSelectedServices,
	CheckoutOrderFragment,
	useCheckinInfoQuery,
	AviaOrder,
	useGetServicesRefundInfoQuery,
	PassengerFragment,
	useOrderQuery
} from '@websky/graphql';
import {
	getOrder,
	getOrderId,
	getRecheckAvailabilityTimout,
	getisAutoReturnEmdPD
} from './store/order/selectors';
import { fillOrder, setOrder } from './store/order/actions';
import { ServicePayload, setMultipleSelectedServices, removeServiceAction } from './store/selectedServices/actions';
import Timeout = NodeJS.Timeout;
import { useIataCode } from '../hooks';
import { calculateCheckinAvailability } from './utils';
import { getPassengersSelectedProductIds } from '../RefundSummary/utils';
import { addLoadingServiceParam, clearLoadingServicesParams } from './store/loadingServicesParams/actions';
import { LoadingServiceType } from './store/loadingServicesParams/reducers';

export const useAvailabilityRecheck = (setActualizationOpen: React.Dispatch<boolean>) => {
	const timeoutToReCheckAvailability = useSelector(getRecheckAvailabilityTimout) * 1000;
	const orderId = useSelector(getOrderId);
	const orderStatus = useSelector(getOrder).status;
	const [timeout, setNewTimeout] = useState<Timeout>(null);
	const dispatch = useDispatch();
	const needToCheckAvailability = orderStatus === OrderStatus.New || orderStatus === OrderStatus.Booked;

	const [recheckAvailability, { data }] = useCheckOrderAvailabilityMutation({ fetchPolicy: 'no-cache' });

	const recheckIfTimoutExceed = () => {
		if (needToCheckAvailability) {
			recheckAvailability({ variables: { id: orderId } });
		}
	};

	useEffect(() => {
		if (!data?.CheckOrderAvailability) {
			return () => setActualizationOpen(false);
		}

		if (data?.CheckOrderAvailability?.status === OrderStatus.Confirmed) {
			clearTimeout(timeout);
			setNewTimeout(null);

			return () => setActualizationOpen(false);
		}

		if (data?.CheckOrderAvailability?.status === OrderStatus.Cancelled) {
			clearTimeout(timeout);
			setNewTimeout(null);

			return () => setActualizationOpen(false);
		}

		dispatch(fillOrder(data.CheckOrderAvailability));
		clearTimeout(timeout);
		setNewTimeout(null);

		if (timeoutToReCheckAvailability === 0) {
			setNewTimeout(setTimeout(recheckIfTimoutExceed, timeoutToReCheckAvailability));
		}

		setActualizationOpen(true);

		return () => {
			setActualizationOpen(false);
		};
	}, [timeoutToReCheckAvailability]);

	useEffect(() => {
		if (!timeout) {
			setNewTimeout(setTimeout(recheckIfTimoutExceed, timeoutToReCheckAvailability));
		}
	}, [timeout, timeoutToReCheckAvailability]);
};

export const useIsCheckinAvailable = (order: CheckoutOrderFragment): boolean => {
	const isConfirmed = order.status === OrderStatus.Confirmed;
	const iataCode = useIataCode();
	const manualCheckIataCodes = ['DV'];

	const { data: getCheckinInfoResponse } = useCheckinInfoQuery({
		skip: !isConfirmed || manualCheckIataCodes.includes(iataCode),
		variables: {
			params: {
				aviaOrderId: order.id
			}
		}
	});

	if (manualCheckIataCodes.includes(iataCode) && isConfirmed) {
		return calculateCheckinAvailability(order as AviaOrder);
	}

	return getCheckinInfoResponse?.CheckinInfo?.isAvailable;
};

export const useServicesRefundInfo = (
	orderId: string,
	travellers: PassengerFragment[],
	selectedPassengerIds: string[],
	selectedSegmentIds: string[],
	isAllFlightToExchange?: boolean,
	skip?: boolean
) => {
	const isAutoReturnEmdPD = useSelector(getisAutoReturnEmdPD);

	const productIds = React.useMemo(() => {
		return getPassengersSelectedProductIds(
			travellers,
			selectedPassengerIds,
			selectedSegmentIds,
			isAllFlightToExchange
		);
	}, [travellers, selectedPassengerIds, selectedSegmentIds, isAllFlightToExchange]);

	return useGetServicesRefundInfoQuery({
		variables: { params: { orderId, productIds } },
		skip: !productIds.length || skip || !isAutoReturnEmdPD
	});
};

export function useOrderServices() {
	const dispatch = useDispatch();

	const [saveOrderServices, saveOrderServicesResult] = useSaveOrderServicesMutation();

	const [requestError, setRequestError] = useState<string>();

	// Effects
	useEffect(() => {
		if (saveOrderServicesResult?.data?.SaveOrderServices) {
			dispatch(fillOrder(saveOrderServicesResult.data.SaveOrderServices));
		}
	}, [saveOrderServicesResult?.data]);

	// Methods
	const onRequestError = useCallback(
		(e: unknown) => {
			if (e instanceof ApolloError) {
				if (e.graphQLErrors?.length) {
					setRequestError(e.graphQLErrors[0].message);
				}
			} else if (e instanceof Error) {
				setRequestError(e.message);
			} else {
				setRequestError('Unknown error');
			}
		},
		[setRequestError]
	);

	const onClearError = useCallback(() => setRequestError(undefined), [setRequestError]);

	const onSaveServicesRequest = useCallback(
		async (params: OrderSelectedServices) => {
			let type: LoadingServiceType;

			if (params.insurances) {
				type = LoadingServiceType.Insurance;
			} else if (params.aeroexpress) {
				type = LoadingServiceType.Transfer;
			}

			if (type) {
				dispatch(addLoadingServiceParam({ type: [type] }));
			}

			try {
				const { data } = await saveOrderServices({ variables: { params } });

				return data?.SaveOrderServices;
			} catch (e) {
				onRequestError(e);

				return null;
			} finally {
				if (type) {
					dispatch(clearLoadingServicesParams());
				}
			}
		},
		[onRequestError, dispatch]
	);

	const onAddServices = useCallback(
		(services: ServicePayload[]) => {
			dispatch(setMultipleSelectedServices(services));
		},
		[dispatch]
	);

	const onRemoveServices = useCallback(
		(services: ServicePayload[]) => {
			services.forEach(serviceToDelete => dispatch(removeServiceAction(serviceToDelete)));
		},
		[dispatch]
	);

	return {
		// data
		requestError,
		saveOrderServicesResult,
		isLoading: saveOrderServicesResult.loading,

		// methods
		saveOrderServices,
		onRequestError,
		onClearError,
		onSaveServicesRequest,
		onAddServices,
		onRemoveServices
	};
}

export function useOrderUpdate() {
	const dispatch = useDispatch();

	const orderId = useSelector(getOrderId);

	const { refetch: getOrder, loading } = useOrderQuery({
		variables: { id: orderId },
		fetchPolicy: 'no-cache',
		skip: true
	});

	const onFetchOrder = async () => {
		try {
			const { data } = await getOrder();

			return data;
		} catch {
			return null;
		}
	};

	const onRefreshOrder = async () => {
		const order = await onFetchOrder();

		if (order?.Order) {
			dispatch(setOrder(order.Order));
		}

		return order;
	};

	return {
		loading,
		onFetchOrder,
		onRefreshOrder
	};
}
